// Home.jsx v3 — Full rebuild
// Chat/dashboard components preserved exactly from v2.
// New marketing sections imported from HomeSections.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RevUpMark from '../components/RevUpMark';
import { Helmet } from 'react-helmet-async';

import {
  ProblemSection,
  ContrastSection,
  HITLSection,
  StatsBand,
  DualCTA,
} from './HomeSections';

import HeroOperated from '../components/home/HeroOperated';
import Problems   from './Problems';
import HowItWorks from './HowItWorks';

/* ─── Brand tokens ─────────────────────────────────────── */
const C  = '#7C3AED';
const R  = '124,58,237';
const C2 = '#00D4AA';
const R2 = '0,212,170';
const BG = '#09060F';

/* ════════════════════════════════════════════════════════════════════════════
   GLOBAL STYLES (keyframes only — kept out of render)
   ════════════════════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;0,900;1,300;1,400;1,700&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
@keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:.45} }
@keyframes fadeIn { from{opacity:0} to{opacity:1} }
@keyframes modalSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
*{box-sizing:border-box;margin:0;padding:0}
body{background:#09060F}
::-webkit-scrollbar{width:0;height:0}
`;

/* ════════════════════════════════════════════════════════════════════════════
   CHAT SCENARIOS
   ════════════════════════════════════════════════════════════════════════════ */
const SCENARIOS = [
  {
    id:'ciso', tab:'AI risk & board reporting', agent:'RevSec', role:'AI CISO',
    color:'#10B981', rgb:'16,185,129', avatar:'RS',
    question:'The board wants a full AI risk report next week. We have no idea what AI is running in our environment. Where do we even start?',
    response:[
      { type:'text', content:'Running full AI environment scan. Connecting to your network, SaaS integrations, cloud workloads and endpoint telemetry…' },
      { type:'pause', ms:1400 },
      { type:'text', content:'This is your overall AI risk exposure across the entire organisation.' },
      { type:'pause', ms:700 },
      { type:'link', dashboard:'exec', label:'AI Governance Executive Dashboard', url:'revsec.revupai.com/exec-dashboard', icon:'chart' },
      { type:'pause', ms:2200 },
      { type:'text', content:'Average risk score is 30.1 across 12 AI tools — but that average is hiding the real problem. ChatGPT Personal Accounts has a risk score of 84.8. Discovered in your environment, never approved. Active critical alert on it right now, DeepSeek data incident still open. DLP is firing: 16 triggers, 36 open violations, 5 blocked. Compliance sitting at 67.7% — 8 gaps.' },
      { type:'pause', ms:1000 },
      { type:'text', content:'Now your AI agents specifically — this is a separate problem to your AI tools.' },
      { type:'pause', ms:800 },
      { type:'link', dashboard:'agentIAM', label:'Agent IAM Dashboard', url:'revsec.revupai.com/agent-iam', icon:'shield' },
      { type:'pause', ms:2200 },
      { type:'text', content:'Agent compliance 66.7%. 8 of 12 governed — but 13 open violations, zero resolved. Two you need to act on: "PII Access Requires Human Oversight" violated 3 times as critical, and "Fully Autonomous Agents Require Guardrails" with 5 violations. Agents making PII decisions with no human in the loop.' },
      { type:'pause', ms:1000 },
      { type:'text', content:'The deepest legal exposure is in your vendor contracts. Grammarly specifically.' },
      { type:'pause', ms:800 },
      { type:'link', dashboard:'vendor', label:'Vendor Contracts · Grammarly Business', url:'revsec.revupai.com/vendors/grammarly', icon:'contract' },
      { type:'pause', ms:2200 },
      { type:'text', content:"Grammarly's own published terms: all submitted text trains their models, no opt-out on the Enterprise plan, data residency US only. No DPA exists. Every document your team runs through Grammarly feeds their model under no legal agreement. Mistral AI same — no DPA, HIGH risk. OpenAI MSA expired 28 February." },
      { type:'pause', ms:1000 },
      { type:'text', content:'Your Q1 board report is ready. Everything above, prioritised by legal exposure, owners assigned.' },
      { type:'pause', ms:700 },
      { type:'link', dashboard:'board', label:'Board Report Q1 2025 — Draft', url:'revsec.revupai.com/reports/q1-2025', icon:'report' },
      { type:'pause', ms:2200 },
      { type:'text', content:'Five actions. Most urgent: GDPR Article 33 supervisory notification within 67 hours — ChatGPT exfiltration, €20M fine risk. DPAs from Mistral, Grammarly and one other within 30 days. CV screening and credit assessment tools need EU AI Act Article 11 documentation — €30M or 6% of global turnover. All assigned, all quantified. Export PDF when ready.' },
      { type:'pause', ms:600 },
      { type:'actions', items:['Export Board Report','Fix GDPR Notification','Obtain Missing DPAs','Review AI Agents'] },
    ],
  },
  {
    id:'architect', tab:'Legacy & AI modernisation', agent:'RevModernize', role:'AI Architect',
    color:'#00D4AA', rgb:'0,212,170', avatar:'RM',
    question:"We want AI running across our core operations but our apps are 10–15 years old. Where do we start and what will it actually take?",
    response:[
      { type:'text', content:'Connecting to your repos now — scanning codebase, mapping stored procedures, dependencies, and running a full AI readiness assessment across your portfolio…' },
      { type:'pause', ms:1600 },
      { type:'text', content:'Discovery complete. 7 files analysed, 103 findings, 17 tables, 16 stored procedures. Your AI Readiness score is 18 out of 100.' },
      { type:'pause', ms:900 },
      { type:'link', dashboard:'aireadiness', label:'AI Readiness Scorecard — healthbridge', url:'revmodernize.revupai.com/healthbridge/readiness', icon:'chart' },
      { type:'pause', ms:2400 },
      { type:'text', content:'Score 18 — not AI-ready. Five critical gaps. No event bus: 29 stored procedures handle writes, so business state changes live inside the database, invisible to any AI agent. No REST APIs, no observability layer, no entity context API, no auth framework for consent. AI cannot connect to, trigger, or learn from any of these systems as they stand.' },
      { type:'pause', ms:1200 },
      { type:'text', content:'I have mapped every stored procedure decision. 16 procedures need a call — move to the API layer, keep in the database, or hybrid. I am surfacing each one to your team for approval before a single line changes.' },
      { type:'pause', ms:900 },
      { type:'link', dashboard:'hitldecisions', label:'HITL Decision Center — 23 pending decisions', url:'revmodernize.revupai.com/healthbridge/hitl', icon:'contract' },
      { type:'pause', ms:2400 },
      { type:'text', content:'Once your team approves those decisions, I build the full stack — 21 production artifacts: FastAPI endpoints, event bus adapters, vector stores, data layers. RevMigrate handles the 5 database-side migration scripts in parallel. No handoff friction — both agents coordinate automatically.' },
      { type:'pause', ms:1100 },
      { type:'text', content:'Your post-transform score projects to 72 out of 100 — AI-ready. That takes 3 to 7 weeks with RevModernize. Without it: 10 to 16 weeks, unassisted.' },
      { type:'pause', ms:800 },
      { type:'link', dashboard:'posttransform', label:'Post-Transform Projection — 18 → 72', url:'revmodernize.revupai.com/healthbridge/projection', icon:'chart' },
      { type:'pause', ms:2400 },
      { type:'text', content:'After the build, AI CISO runs automatically — compliance posture check on everything shipped, missing controls flagged before anything reaches production. Shall I generate the full modernisation roadmap with HITL decision points?' },
      { type:'pause', ms:600 },
      { type:'actions', items:['Generate Roadmap','View HITL Decisions','Review Critical Gaps','Start Discovery'] },
    ],
  },
  {
    id:'dba', tab:'Database cost & migration', agent:'RevMigrate', role:'AI DBA',
    color:'#F59E0B', rgb:'245,158,11', avatar:'RD',
    question:'Our Oracle renewal is in 90 days and the number is eye-watering. What would it actually cost to move off it and is it even realistic in that timeframe?',
    response:[
      { type:'text', content:'Running AI Scout on Oracle_Prod_DB now — scanning schema, stored procedures, dependencies, data volumes, and current licence cost…' },
      { type:'pause', ms:1200 },
      { type:'text', content:'Scan complete. 8,729 objects discovered. Readiness score 100/100 — zero blockers. Here is the full environment analysis.' },
      { type:'pause', ms:700 },
      { type:'link', dashboard:'revmigrate', label:'AI DBA Portfolio Dashboard — Oracle_Prod_DB', url:'revmigrate.revupai.com/Oracle_Prod_DB', icon:'database' },
      { type:'pause', ms:2000 },
      { type:'text', content:'PostgreSQL 16 handles your full schema cleanly. Of the 340 stored procedures, 303 convert automatically — 89%. The remaining 37 need an architectural decision. I will surface each one to your team for approval before anything moves. Not a single object changes without a human sign-off.' },
      { type:'pause', ms:900 },
      { type:'text', content:'On effort: AI Scout shows 88% less time than a traditional migration — 1,018 hours with RevMigrate vs 8,448 hours manually. That is ~$1.1M in saved engineering cost on this migration alone.' },
      { type:'pause', ms:700 },
      { type:'link', dashboard:'aiscout', label:'AI Scout — Migration Effort Estimate', url:'revmigrate.revupai.com/ai-scout', icon:'chart' },
      { type:'pause', ms:2000 },
      { type:'text', content:'On cost: your current Oracle licensing is $17,600 per year. Managed PostgreSQL brings that to $4,800 — a 73% reduction, ROI in 8 months.' },
      { type:'pause', ms:700 },
      { type:'link', dashboard:'costimpact', label:'Cost Impact — Oracle_Prod_DB → PostgreSQL', url:'revmigrate.revupai.com/cost-impact', icon:'cost' },
      { type:'pause', ms:2000 },
      { type:'text', content:'90 days is achievable. Migration runs in four phases — Plan, Convert, Deploy, Validate — with AI-verified checkpoints and human sign-off at every critical stage. I provision and manage your PostgreSQL environment after cutover. You never need a DBA on staff.' },
      { type:'pause', ms:600 },
      { type:'actions', items:['Generate Migration Proposal','Start Pipeline','Review HITL Decisions','Provision Cloud Environment'] },
    ],
  },
];

const TYPE_SPEED_Q = 42;
const TYPE_SPEED_A = 28;
const PAUSE_BEFORE = 800;
const LOOP_PAUSE   = 5000;

/* ════════════════════════════════════════════════════════════════════════════
   DASHBOARD LIGHT COMPONENTS
   ════════════════════════════════════════════════════════════════════════════ */
const D = {
  bg:'#F4F3F8', card:'#FFFFFF', border:'#E8E6F0', text:'#1A1035',
  sub:'#6B6880', faint:'#9B98A8', crit:'#DC2626', warn:'#D97706',
  ok:'#059669', purple:'#7C3AED', teal:'#0891B2',
};
const Badge = ({ label, color='#DC2626', bg }) => (
  <span style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', color, background:bg||color+'18', border:`1px solid ${color}30` }}>{label}</span>
);
const StatTile = ({ label, value, sub, color=D.text, small }) => (
  <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:'14px 16px' }}>
    <div style={{ fontSize:small?20:26, fontWeight:800, color, lineHeight:1, marginBottom:4 }}>{value}</div>
    {sub && <div style={{ fontSize:10, color:D.ok, fontWeight:600, marginBottom:2 }}>{sub}</div>}
    <div style={{ fontSize:11, color:D.sub }}>{label}</div>
  </div>
);

function ExecDashboard() {
  const heatmap = [
    { name:'ChatGPT (Personal Accounts)', inventory:90, data:85, model:70, compliance:95, overall:84.8, trend:'↑', trendColor:D.crit },
    { name:'GitHub Copilot Business',     inventory:15, data:22, model:10, compliance:15, overall:15.8, trend:'—', trendColor:D.sub },
    { name:'Grammarly Business',          inventory:10, data:15, model: 5, compliance:10, overall:10.2, trend:'—', trendColor:D.sub },
    { name:'AWS Bedrock',                 inventory: 8, data:10, model:15, compliance: 5, overall: 9.6, trend:'↓', trendColor:D.ok  },
  ];
  const heatColor = v => v>=80?'#FEE2E2':v>=60?'#FEF3C7':v>=40?'#FFF7ED':'#DCFCE7';
  const heatText  = v => v>=80?D.crit:v>=60?D.warn:v>=40?'#EA580C':D.ok;
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:D.bg, minHeight:'100%', display:'flex' }}>
      <div style={{ width:220, background:D.card, borderRight:`1px solid ${D.border}`, padding:'20px 0', flexShrink:0 }}>
        <div style={{ padding:'0 16px 16px', borderBottom:`1px solid ${D.border}`, marginBottom:16 }}>
          <select style={{ width:'100%', padding:'6px 10px', borderRadius:6, border:`1px solid ${D.border}`, fontSize:12, color:D.text, background:D.card }}><option>Executive Overview</option></select>
        </div>
        <div style={{ padding:'0 16px', marginBottom:20 }}>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:2, textTransform:'uppercase', color:D.faint, marginBottom:10 }}>Risk Portfolio</div>
          {[['ChatGPT (Personal Accounts)','discovered',84.8,D.crit],['GitHub Copilot Business','approved',15.8,D.ok],['Grammarly Business','approved',10.2,D.ok],['AWS Bedrock','approved',9.6,D.ok]].map(([n,s,v,c])=>(
            <div key={n} style={{ padding:'8px 0', borderBottom:`1px solid ${D.border}` }}>
              <div style={{ fontSize:11, fontWeight:600, color:D.text, marginBottom:2, lineHeight:1.3 }}>{n}</div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:10, color:D.faint }}>{s}</span>
                <span style={{ fontSize:12, fontWeight:800, color:c }}>{s==='discovered'?'+':'-'} {v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:24 }}>
        <div style={{ marginBottom:20 }}>
          <h2 style={{ fontSize:20, fontWeight:800, color:D.text, margin:'0 0 4px' }}>AI Governance Executive Dashboard</h2>
          <p style={{ fontSize:12, color:D.sub, margin:0 }}>Cross-module risk posture, alerts, and compliance overview</p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:16 }}>
          <StatTile label="Avg Risk Score" value="30.1" color={D.warn}/>
          <StatTile label="AI Tools" value="12" sub="5 approved" color={D.teal}/>
          <StatTile label="Active Alerts" value="10" sub="4 critical" color={D.crit}/>
          <StatTile label="Compliance" value="67.7%" color={D.warn}/>
          <StatTile label="Threats Blocked" value="5" sub="2 critical" color={D.purple}/>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:D.purple, marginBottom:12 }}>📋 AI Inventory</div>
            <div style={{ display:'flex', gap:24 }}>
              {[['5','Approved',D.ok],['2','Review',D.warn],['4','Shadow/Ban',D.crit]].map(([v,l,c])=>(
                <div key={l}><div style={{ fontSize:22, fontWeight:800, color:c }}>{v}</div><div style={{ fontSize:10, color:D.sub }}>{l}</div></div>
              ))}
            </div>
          </div>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:D.teal, marginBottom:12 }}>〜 Data Flows & DLP</div>
            <div style={{ display:'flex', gap:24 }}>
              {[['16','DLP Triggers',D.text],['36','Open Violations',D.crit],['5','Blocked',D.ok]].map(([v,l,c])=>(
                <div key={l}><div style={{ fontSize:22, fontWeight:800, color:c }}>{v}</div><div style={{ fontSize:10, color:D.sub }}>{l}</div></div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:'16px', marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>Risk Heatmap</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead><tr>{['Tool','Inventory','Data','Model','Compliance','Overall','Trend'].map(h=>(
              <th key={h} style={{ textAlign:h==='Tool'?'left':'center', padding:'6px 8px', color:D.faint, fontWeight:600, borderBottom:`1px solid ${D.border}` }}>{h}</th>
            ))}</tr></thead>
            <tbody>{heatmap.map(row=>(
              <tr key={row.name}>
                <td style={{ padding:'8px', color:D.text, fontWeight:500, fontSize:11 }}>{row.name}</td>
                {[row.inventory,row.data,row.model,row.compliance].map((v,i)=>(
                  <td key={i} style={{ textAlign:'center', padding:'6px 4px' }}>
                    <span style={{ background:heatColor(v), color:heatText(v), padding:'3px 8px', borderRadius:4, fontWeight:700, fontSize:11 }}>{v}</span>
                  </td>
                ))}
                <td style={{ textAlign:'center', padding:'6px 8px', fontWeight:800, color:heatText(row.overall), fontSize:13 }}>{row.overall}</td>
                <td style={{ textAlign:'center', padding:'6px 8px', fontWeight:700, color:row.trendColor, fontSize:14 }}>{row.trend}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <span style={{ fontSize:16 }}>🚨</span>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:D.crit }}>Critical Risk: ChatGPT Personal Accounts exceed risk threshold</div>
              <div style={{ fontSize:11, color:D.sub }}>risk engine · 27d ago</div>
            </div>
          </div>
          <Badge label="Acknowledged" color={D.sub}/>
        </div>
      </div>
    </div>
  );
}

function AgentIAMDashboard() {
  const policies = [
    { name:'Fully Autonomous Agents Require Guardrails', sev:'High',     sevColor:D.warn, count:5 },
    { name:'PII Access Requires Human Oversight',        sev:'Critical', sevColor:D.crit, count:3 },
    { name:'High Risk Agents Require Owner',             sev:'High',     sevColor:D.warn, count:2 },
    { name:'Shadow Agents Must Be Reviewed',             sev:'Medium',   sevColor:'#D97706', count:2 },
    { name:'Critical Risk Agents Auto-Quarantine',       sev:'Critical', sevColor:D.crit, count:1 },
  ];
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:D.bg, minHeight:'100%' }}>
      <div style={{ background:D.card, borderBottom:`1px solid ${D.border}`, padding:'16px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
          <div style={{ width:32, height:32, background:'#EEF2FF', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🛡️</div>
          <div>
            <h2 style={{ fontSize:18, fontWeight:800, color:D.text, margin:0 }}>Agent IAM</h2>
            <p style={{ fontSize:11, color:D.sub, margin:0 }}>Identity & access management for AI agents</p>
          </div>
        </div>
        <div style={{ display:'flex', gap:4, marginTop:12 }}>
          {['Dashboard','Policies','Violations 13','Alerts','Approvals'].map((t,i)=>(
            <div key={t} style={{ padding:'6px 14px', fontSize:12, fontWeight:i===0?700:400, color:i===0?D.purple:D.sub, borderBottom:i===0?`2px solid ${D.purple}`:'2px solid transparent', cursor:'pointer' }}>{t}</div>
          ))}
        </div>
      </div>
      <div style={{ padding:24 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
          <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:28, fontWeight:900, color:D.warn }}>66.7%</div>
            <div style={{ fontSize:11, color:D.sub, marginTop:2 }}>Compliance Score</div>
          </div>
          <StatTile label="Active Policies" value="11" sub="Governing 12 agents" color={D.text}/>
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:28, fontWeight:900, color:D.crit }}>13</div>
            <div style={{ fontSize:11, color:D.sub, marginTop:2 }}>Open Violations</div>
          </div>
          <StatTile label="Total Resolved" value="0" color={D.sub}/>
        </div>
        <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>Most Violated Policies</div>
          {policies.map((p,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', marginBottom:6, background:D.bg, borderRadius:8, border:`1px solid ${D.border}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:14 }}>{p.sev==='Critical'?'🚫':'⚠️'}</span>
                <span style={{ fontSize:12, color:D.text, fontWeight:500 }}>{p.name}</span>
                <Badge label={p.sev} color={p.sevColor}/>
              </div>
              <span style={{ fontSize:12, fontWeight:700, color:D.sub }}>{p.count} violations</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VendorDashboard() {
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:D.bg, minHeight:'100%', display:'flex' }}>
      <div style={{ width:220, background:D.card, borderRight:`1px solid ${D.border}`, padding:20, flexShrink:0 }}>
        <button style={{ width:'100%', padding:'8px 12px', background:D.purple, color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:16 }}>+ Add Vendor</button>
        {[['OpenAI','LOW','✓ DPA',D.ok],['Mistral AI','HIGH','△ No DPA',D.crit],['AWS (Bedrock)','LOW','✓ DPA',D.ok],['Grammarly Business','CRITICAL','✗ No DPA',D.crit]].map(([n,r,d,c])=>(
          <div key={n} style={{ padding:'10px 12px', marginBottom:6, borderRadius:8, background:n==='Grammarly Business'?'#FEF2F2':D.bg, border:`1px solid ${n==='Grammarly Business'?'#FECACA':D.border}`, cursor:'pointer' }}>
            <div style={{ fontSize:12, fontWeight:600, color:D.text, marginBottom:4 }}>{n}</div>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:10, color:c, fontWeight:700 }}>{d}</span>
              <Badge label={r} color={c}/>
            </div>
          </div>
        ))}
      </div>
      <div style={{ flex:1, overflow:'auto', padding:24 }}>
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          <Badge label="CRITICAL" color={D.crit}/>
          <Badge label="Trains on data" color="#B45309" bg="#FEF3C7"/>
          <Badge label="No DPA" color={D.crit}/>
        </div>
        <h2 style={{ fontSize:22, fontWeight:800, color:D.text, margin:'0 0 4px' }}>Grammarly Business</h2>
        <div style={{ background:'#F8F6FF', border:'1px solid #E9D5FF', borderRadius:10, padding:16, margin:'20px 0 16px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:D.purple, marginBottom:8 }}>✨ Public Terms Summary</div>
          <p style={{ fontSize:12, color:D.sub, lineHeight:1.6, margin:'0 0 12px' }}>Grammarly uses submitted text to train and improve its AI models. No opt-out available for Enterprise plan in standard terms. Data residency US only.</p>
        </div>
        <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:D.text, marginBottom:12 }}>⚠️ Public Terms Risk Flags</div>
          {[
            ['Trains on customer data by default','CRITICAL',D.crit,'#FEF2F2','#FECACA','All text submitted is used for model training with no opt-out'],
            ['No opt-out available','CRITICAL',D.crit,'#FEF2F2','#FECACA','Cannot prevent training data use in standard Enterprise plan'],
            ['Data residency US only','MEDIUM',D.warn,'#FFFBEB','#FDE68A','US processing only — SCCs required'],
          ].map(([title,sev,c,bg,border,desc])=>(
            <div key={title} style={{ background:bg, border:`1px solid ${border}`, borderRadius:8, padding:'12px 14px', marginBottom:8 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
                <span style={{ fontSize:12, fontWeight:600, color:c }}>{title}</span>
                <Badge label={sev} color={c}/>
              </div>
              <div style={{ fontSize:11, color:D.sub }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BoardReportDashboard() {
  const recs = [
    { n:1, title:'Submit GDPR Article 33 notification within 67 hours', tags:['Immediate','Legal'],   tagColor:D.crit,   color:'#FEF2F2', border:'#FECACA', desc:'The ChatGPT data exfiltration incident requires immediate supervisory authority notification.', risk:'Risk: Up to €20M fine under GDPR Article 83(4)' },
    { n:2, title:'Obtain DPAs from 3 vendors processing personal data',  tags:['30 days','Legal'],    tagColor:D.warn,   color:'#FFFBEB', border:'#FDE68A', desc:'Mistral AI, Grammarly, and one additional vendor are processing data without DPAs.', risk:'Risk: GDPR Article 28 violation' },
    { n:3, title:'Block or approve 6 shadow AI tools',                   tags:['30 days','CISO'],    tagColor:D.warn,   color:'#FFFBEB', border:'#FDE68A', desc:'6 unapproved tools in active use represent uncontrolled data exposure.', risk:'Risk: Uncontrolled IP and customer data exposure' },
    { n:4, title:'Complete EU AI Act documentation for 2 high-risk systems', tags:['90 days','CISO'], tagColor:D.purple, color:'#F5F3FF', border:'#DDD6FE', desc:'CV screening tool and credit assessment model require Article 11 technical documentation.', risk:'Risk: EU AI Act fines up to €30M or 6% global turnover' },
    { n:5, title:"Escalate Sales department policy acknowledgement",      tags:['30 days','HR'],      tagColor:D.ok,     color:'#F0FDF4', border:'#BBF7D0', desc:"51% acknowledgement in Sales is the lowest across all departments.", risk:'Risk: Policy non-compliance creates audit and regulatory exposure' },
  ];
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:D.bg, minHeight:'100%', display:'flex' }}>
      <div style={{ width:200, background:D.card, borderRight:`1px solid ${D.border}`, padding:16, flexShrink:0 }}>
        <div style={{ padding:'10px 12px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8 }}>
          <div style={{ fontSize:12, fontWeight:700, color:D.text, marginBottom:2 }}>Board Report</div>
          <div style={{ fontSize:12, fontWeight:600, color:D.warn }}>Q1 2025</div>
          <div style={{ fontSize:10, color:D.faint }}>Score 67/100 · Draft</div>
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ display:'flex', gap:8 }}><Badge label="Board Report" color={D.purple}/><Badge label="Draft" color={D.warn}/></div>
          <div style={{ display:'flex', gap:8 }}>
            <button style={{ padding:'6px 14px', background:D.warn, color:'#fff', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>🚩 Mark Final</button>
            <button style={{ padding:'6px 14px', background:D.bg, color:D.text, border:`1px solid ${D.border}`, borderRadius:6, fontSize:11, cursor:'pointer' }}>↓ Export PDF</button>
          </div>
        </div>
        <h2 style={{ fontSize:22, fontWeight:800, color:D.text, margin:'0 0 20px' }}>Q1 2025 — AI Governance Board Report</h2>
        <div style={{ display:'flex', gap:20, alignItems:'center', marginBottom:24, background:D.card, border:`1px solid ${D.border}`, borderRadius:12, padding:'16px 20px' }}>
          <div style={{ position:'relative', width:70, height:70, flexShrink:0 }}>
            <svg viewBox="0 0 36 36" style={{ width:70, height:70, transform:'rotate(-90deg)' }}>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={D.border} strokeWidth="3"/>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={D.warn} strokeWidth="3" strokeDasharray="67 33" strokeLinecap="round"/>
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:900, color:D.warn }}>67</div>
          </div>
          <div style={{ display:'flex', gap:20, flex:1, flexWrap:'wrap' }}>
            {[['34','AI TOOLS'],['6','SHADOW AI'],['74%','POLICY ACK'],['3','OPEN INCIDENTS'],['1','GDPR PENDING'],['3','MISSING DPAS']].map(([v,l])=>(
              <div key={l} style={{ textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:900, color:l.includes('SHADOW')||l.includes('PENDING')||l.includes('DPA')?D.crit:l.includes('POLICY')?D.ok:D.text }}>{v}</div>
                <div style={{ fontSize:9, fontWeight:700, color:D.faint, letterSpacing:.8 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>⚡ Board Recommendations (5)</div>
        {recs.map(r=>(
          <div key={r.n} style={{ background:r.color, border:`1px solid ${r.border}`, borderRadius:10, padding:'14px 16px', marginBottom:10, display:'flex', gap:14 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:r.tagColor, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, flexShrink:0 }}>{r.n}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', gap:6, marginBottom:6, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:13, fontWeight:700, color:D.text }}>{r.title}</span>
                {r.tags.map(t=><Badge key={t} label={t} color={r.tagColor}/>)}
              </div>
              <p style={{ fontSize:11, color:D.sub, margin:'0 0 4px', lineHeight:1.5 }}>{r.desc}</p>
              <p style={{ fontSize:10, fontStyle:'italic', color:r.tagColor, margin:0 }}>{r.risk}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevMigrateDashboard({ initialTab='overview' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const tabs = ['Overview','AI Scout','Cost Impact','Migration Journey'];
  const effortRows = [
    { cat:'Schema Conversion',    objects:3275, with:132,  without:940,  saving:'86%', risk:'LOW' },
    { cat:'Stored Procedures',    objects:122,  with:15,   without:220,  saving:'93%', risk:'LOW' },
    { cat:'Functions & Views',    objects:4437, with:210,  without:1840, saving:'89%', risk:'LOW' },
    { cat:'Data Migration',       objects:null, with:480,  without:3200, saving:'85%', risk:'MEDIUM' },
    { cat:'Testing & Validation', objects:null, with:181,  without:2248, saving:'92%', risk:'LOW' },
  ];
  const riskColor = r => r==='LOW'?D.ok:r==='MEDIUM'?D.warn:D.crit;
  const riskBg    = r => r==='LOW'?'#DCFCE7':r==='MEDIUM'?'#FEF3C7':'#FEE2E2';

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:D.bg, minHeight:'100%', display:'flex', flexDirection:'column' }}>
      {/* Top bar */}
      <div style={{ background:D.card, borderBottom:`1px solid ${D.border}`, padding:'14px 24px', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
          <div style={{ width:36, height:36, background:'#FFFBEB', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, border:'1px solid #FDE68A' }}>🗄️</div>
          <div>
            <h2 style={{ fontSize:17, fontWeight:800, color:D.text, margin:0 }}>RevMigrate · AI DBA</h2>
            <p style={{ fontSize:11, color:D.sub, margin:0 }}>Oracle_Prod_DB → PostgreSQL · Production · Updated just now</p>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ padding:'3px 10px', background:'#DCFCE7', border:'1px solid #BBF7D0', borderRadius:100, fontSize:10, fontWeight:700, color:D.ok, letterSpacing:.8 }}>● HEALTHY</span>
            <span style={{ padding:'3px 10px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:100, fontSize:10, fontWeight:700, color:D.warn }}>READINESS 100/100</span>
          </div>
        </div>
        <div style={{ display:'flex', gap:0 }}>
          {tabs.map((t)=>{
            const key = t.toLowerCase().replace(/ /g,'_');
            const active = activeTab===key;
            return (
              <button key={t} onClick={()=>setActiveTab(key)}
                style={{ padding:'7px 18px', fontSize:12, fontWeight:active?700:400,
                  color:active?'#D97706':D.sub,
                  background:'none', border:'none', borderBottom:active?'2px solid #D97706':'2px solid transparent',
                  cursor:'pointer' }}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex:1, overflow:'auto', padding:24 }}>

        {/* ── OVERVIEW TAB ── */}
        {activeTab==='overview' && <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:16 }}>
            <StatTile label="Connected DBs"      value="2"       color={D.teal}/>
            <StatTile label="Objects Discovered" value="8,729"   color={D.text}/>
            <StatTile label="Readiness Score"    value="100/100" color={D.ok} sub="AI-ready"/>
            <StatTile label="Database Health"    value="92/100"  color={D.ok}/>
            <StatTile label="Risk Level"         value="LOW"     color={D.ok}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>📋 Database Priorities</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead><tr>{['Database','Env','Type','Readiness','Status'].map(h=>(
                  <th key={h} style={{ textAlign:'left', padding:'6px 8px', color:D.faint, fontWeight:600, borderBottom:`1px solid ${D.border}`, fontSize:10, letterSpacing:.5 }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  <tr>
                    <td style={{ padding:'10px 8px', fontWeight:600, color:D.text, fontSize:12 }}>Oracle_Prod_DB</td>
                    <td style={{ padding:'10px 8px' }}><Badge label="PRODUCTION" color={D.purple}/></td>
                    <td style={{ padding:'10px 8px' }}><Badge label="SQL SERVER" color={D.teal}/></td>
                    <td style={{ padding:'10px 8px', fontWeight:800, color:D.ok, fontSize:13 }}>100</td>
                    <td style={{ padding:'10px 8px' }}><Badge label="Ready →" color={D.ok}/></td>
                  </tr>
                  <tr>
                    <td style={{ padding:'10px 8px', fontWeight:600, color:D.text, fontSize:12 }}>postgres</td>
                    <td style={{ padding:'10px 8px' }}><Badge label="PRODUCTION" color={D.purple}/></td>
                    <td style={{ padding:'10px 8px' }}><Badge label="POSTGRESQL" color={D.ok}/></td>
                    <td style={{ padding:'10px 8px', fontWeight:600, color:D.faint, fontSize:12 }}>—</td>
                    <td style={{ padding:'10px 8px', fontSize:11, color:D.sub }}>Target</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>🔥 Portfolio Risk Heatmap</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead><tr>{['Database','Schema','Types','Procs','Deps','Volume','Overall'].map(h=>(
                  <th key={h} style={{ textAlign:'center', padding:'5px 6px', color:D.faint, fontWeight:600, borderBottom:`1px solid ${D.border}`, fontSize:10 }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  <tr>
                    <td style={{ padding:'8px 6px', fontWeight:600, color:D.text, fontSize:11 }}>Oracle_Prod_DB</td>
                    {[100,100,92,97,100].map((v,i)=>(
                      <td key={i} style={{ textAlign:'center', padding:'6px 4px' }}>
                        <span style={{ background:'#DCFCE7', color:D.ok, padding:'3px 7px', borderRadius:4, fontWeight:700, fontSize:11 }}>{v}</span>
                      </td>
                    ))}
                    <td style={{ textAlign:'center', fontWeight:900, color:D.ok, fontSize:14 }}>100</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>⚡ Agent Actions</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
              {[['🔄','Migration Agent','Converts database objects'],['🔗','Dependency Mapper','Maps object relationships'],['🩺','Health Monitor','Database performance tracking'],['⚡','Query Optimizer','AI query performance tuning']].map(([icon,name,desc])=>(
                <div key={name} style={{ background:D.bg, border:`1px solid ${D.border}`, borderRadius:9, padding:'12px 14px', display:'flex', gap:10, alignItems:'flex-start' }}>
                  <span style={{ fontSize:16 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:D.text, marginBottom:2 }}>{name}</div>
                    <div style={{ fontSize:10, color:D.sub, marginBottom:4 }}>{desc}</div>
                    <Badge label="IDLE" color={D.sub}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>}

        {/* ── AI SCOUT TAB ── */}
        {activeTab==='ai_scout' && <>
          <div style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:16, fontWeight:800, color:D.text, margin:'0 0 4px' }}>Migration Effort Estimate</h3>
            <p style={{ fontSize:12, color:D.sub, margin:0 }}>Oracle_Prod_DB → PostgreSQL — RevMigrate vs Traditional Manual Development</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:20 }}>
            <div style={{ background:D.card, border:`2px solid #E9D5FF`, borderRadius:10, padding:'16px 20px' }}>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, color:D.purple, marginBottom:8 }}>⚡ WITH REVMIGRATE</div>
              <div style={{ fontSize:36, fontWeight:900, color:D.purple, lineHeight:1 }}>1,018</div>
              <div style={{ fontSize:11, color:D.sub, marginTop:4 }}>HOURS · ~25.5 weeks</div>
              <div style={{ fontSize:10, color:D.faint, marginTop:4 }}>Agents + Expert Review</div>
            </div>
            <div style={{ background:'#F0FDF4', border:'2px solid #BBF7D0', borderRadius:10, padding:'16px 20px', textAlign:'center' }}>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, color:D.ok, marginBottom:8 }}>TIME SAVED</div>
              <div style={{ fontSize:36, fontWeight:900, color:D.ok, lineHeight:1 }}>88%</div>
              <div style={{ fontSize:11, color:D.ok, marginTop:4, fontWeight:600 }}>7,430 HOURS SAVED</div>
              <div style={{ fontSize:10, color:D.sub, marginTop:2 }}>~$1.1M estimated savings</div>
            </div>
            <div style={{ background:'#FEF2F2', border:'2px solid #FECACA', borderRadius:10, padding:'16px 20px' }}>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, color:D.crit, marginBottom:8 }}>WITHOUT REVMIGRATE</div>
              <div style={{ fontSize:36, fontWeight:900, color:D.crit, lineHeight:1 }}>8,448</div>
              <div style={{ fontSize:11, color:D.sub, marginTop:4 }}>HOURS · ~211.2 weeks</div>
              <div style={{ fontSize:10, color:D.faint, marginTop:4 }}>Traditional Manual Development</div>
            </div>
          </div>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:D.text, marginBottom:12 }}>Effort Breakdown by Category</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>{['Category','Objects','With RevMigrate','Without RevMigrate','Savings','Risk'].map(h=>(
                <th key={h} style={{ textAlign:h==='Category'?'left':'center', padding:'8px 10px', color:D.faint, fontWeight:600, borderBottom:`1px solid ${D.border}`, fontSize:11 }}>{h}</th>
              ))}</tr></thead>
              <tbody>{effortRows.map((r,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${D.border}` }}>
                  <td style={{ padding:'10px', fontWeight:600, color:D.text }}>{r.cat}</td>
                  <td style={{ padding:'10px', textAlign:'center', color:D.sub }}>{r.objects||'—'}</td>
                  <td style={{ padding:'10px', textAlign:'center', fontWeight:700, color:D.purple }}>{r.with} hrs</td>
                  <td style={{ padding:'10px', textAlign:'center', color:D.sub }}>{r.without} hrs</td>
                  <td style={{ padding:'10px', textAlign:'center' }}><span style={{ background:'#DCFCE7', color:D.ok, padding:'3px 10px', borderRadius:100, fontWeight:700, fontSize:11 }}>{r.saving}</span></td>
                  <td style={{ padding:'10px', textAlign:'center' }}><span style={{ background:riskBg(r.risk), color:riskColor(r.risk), padding:'3px 10px', borderRadius:100, fontWeight:700, fontSize:10 }}>{r.risk}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>}

        {/* ── COST IMPACT TAB ── */}
        {activeTab==='cost_impact' && <>
          <div style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:16, fontWeight:800, color:D.text, margin:'0 0 4px' }}>Cost Impact Analysis</h3>
            <p style={{ fontSize:12, color:D.sub, margin:0 }}>Oracle_Prod_DB — Current licensing vs PostgreSQL (Managed)</p>
          </div>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:12, padding:'24px 28px', marginBottom:16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:16, alignItems:'center', marginBottom:20 }}>
              <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'16px 20px' }}>
                <div style={{ fontSize:10, fontWeight:700, color:D.faint, letterSpacing:1, marginBottom:8 }}>CURRENT LICENSING</div>
                <div style={{ fontSize:28, fontWeight:900, color:D.crit, textDecoration:'line-through', opacity:.8 }}>$17,600<span style={{ fontSize:14, fontWeight:500 }}>/yr</span></div>
              </div>
              <div style={{ fontSize:24, color:D.ok, fontWeight:700 }}>→</div>
              <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'16px 20px' }}>
                <div style={{ fontSize:10, fontWeight:700, color:D.faint, letterSpacing:1, marginBottom:8 }}>POSTGRESQL (MANAGED)</div>
                <div style={{ fontSize:28, fontWeight:900, color:D.ok }}>$4,800<span style={{ fontSize:14, fontWeight:500 }}>/yr</span></div>
              </div>
            </div>
            <div style={{ textAlign:'center', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'20px 24px' }}>
              <div style={{ fontSize:36, fontWeight:900, color:D.ok }}>$12,800</div>
              <div style={{ fontSize:12, color:D.sub, marginTop:4 }}>estimated annual savings</div>
              <div style={{ display:'flex', gap:16, justifyContent:'center', marginTop:12 }}>
                <span style={{ fontSize:12, fontWeight:700, color:D.ok }}>73% reduction</span>
                <span style={{ fontSize:12, color:D.faint }}>·</span>
                <span style={{ fontSize:12, fontWeight:700, color:D.teal }}>ROI ~8 months</span>
              </div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <StatTile label="Oracle Licence Saving" value="$1.6M"  color={D.ok} sub="Per year"/>
            <StatTile label="DBA Operations Saving" value="$280K"  color={D.ok} sub="Per year"/>
            <StatTile label="Total Annual Saving"   value="$1.88M" color={D.ok} sub="Payback < 3 months"/>
          </div>
        </>}

        {/* ── MIGRATION JOURNEY TAB ── */}
        {activeTab==='migration_journey' && <>
          <div style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:16, fontWeight:800, color:D.text, margin:'0 0 4px' }}>Migration Journey</h3>
            <p style={{ fontSize:12, color:D.sub, margin:0 }}>Oracle_Prod_DB → PostgreSQL · Phase-by-phase progress</p>
          </div>
          <div style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:10, padding:20, marginBottom:16 }}>
            {[
              { phase:'Plan',     pct:100, status:'X-Ray complete',          color:D.ok,    objects:'—' },
              { phase:'Convert',  pct:0,   status:'Waiting for conversion',  color:D.warn,  objects:'0/4559 objects' },
              { phase:'Deploy',   pct:0,   status:'Waiting for conversion',  color:D.faint, objects:'—' },
              { phase:'Validate', pct:0,   status:'Waiting for deployment',  color:D.faint, objects:'—' },
            ].map((p,i)=>(
              <div key={i} style={{ marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:p.pct===100?D.ok:p.pct>0?D.warn:D.faint }}>{p.phase}</span>
                    <span style={{ fontSize:11, color:D.sub }}>{p.status}</span>
                  </div>
                  <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <span style={{ fontSize:11, color:D.faint }}>{p.objects}</span>
                    <span style={{ fontSize:12, fontWeight:800, color:p.color }}>{p.pct}%</span>
                  </div>
                </div>
                <div style={{ height:6, background:D.bg, borderRadius:100, overflow:'hidden', border:`1px solid ${D.border}` }}>
                  <div style={{ height:'100%', width:`${p.pct}%`, background:p.color, borderRadius:100 }}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:'14px 18px', display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ fontSize:18 }}>💡</span>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:D.text, marginBottom:2 }}>Next best action</div>
              <div style={{ fontSize:12, color:D.sub }}>Start migration waves for top-ready databases — Oracle_Prod_DB is at 100/100 readiness with zero blockers reported.</div>
            </div>
            <button style={{ marginLeft:'auto', padding:'8px 16px', background:'#D97706', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', flexShrink:0 }}>Start Pipeline →</button>
          </div>
        </>}

      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   REVMODERNIZE DASHBOARDS — dark-themed, matching product UI
   ════════════════════════════════════════════════════════════════════════════ */
const DK = {
  bg:'#0D0B14', panel:'#13101F', card:'#1A1530', border:'rgba(255,255,255,0.08)',
  text:'#EDE9F8', sub:'#9B90BA', faint:'#5A5278', teal:'#00D4AA', purple:'#7C3AED',
  crit:'#EF4444', warn:'#F59E0B', ok:'#10B981', blue:'#60A5FA',
};
const DKBadge = ({ label, color, bg }) => (
  <span style={{ padding:'2px 7px', borderRadius:4, fontSize:9, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', color:color||DK.text, background:bg||`${color||DK.text}18`, border:`1px solid ${color||DK.text}30` }}>{label}</span>
);

function RevModernizeAIReadinessDashboard() {
  const gaps = [
    '[Data Readiness] Structured event emission: 29 stored procs handle writes — business state changes live inside the DB, invisible to any event bus.',
    '[API & Agent Integration] Action APIs (write / trigger): No REST framework — must build API layer before AI agents can interact.',
    '[Observability & Feedback] Business outcome tracking: No logging or observability — AI cannot learn from outcomes it cannot observe.',
    '[Context & Memory] Entity context API: No API — entity context cannot be served to AI agents securely.',
    '[Governance & Trust] Consent & permissions propagation: No auth framework — AI cannot safely determine which data it may process.',
  ];
  const dims = [
    { label:'Data Readiness',          score:20, max:100, color:DK.crit },
    { label:'API & Agent Integration', score:15, max:100, color:DK.crit },
    { label:'Observability & Feedback',score:10, max:100, color:DK.crit },
    { label:'Context & Memory',        score:10, max:100, color:DK.warn },
    { label:'Governance & Trust',      score:35, max:100, color:DK.warn },
  ];
  const projects = [
    { name:'healthbridge', org:'Healthbridge', score:18, status:'Not AI-Ready', statusColor:DK.crit, active:true },
    { name:'Test Bank Oracle forms', org:'Test Org', score:null },
    { name:'Bank Oracle Forms Modernization', org:'Test Org', score:null },
  ];
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:DK.bg, minHeight:'100%', display:'flex', color:DK.text }}>
      {/* Left sidebar */}
      <div style={{ width:200, background:DK.panel, borderRight:`1px solid ${DK.border}`, padding:'16px 0', flexShrink:0, display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'0 14px 12px', borderBottom:`1px solid ${DK.border}`, marginBottom:12 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:DK.faint, marginBottom:10 }}>PROJECTS</div>
          {projects.map((p,i)=>(
            <div key={i} style={{ padding:'10px 12px', marginBottom:4, borderRadius:8, background:p.active?`rgba(0,212,170,0.08)`:'transparent', border:`1px solid ${p.active?`rgba(0,212,170,0.2)`:'transparent'}`, cursor:'pointer' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
                <span style={{ fontSize:12, fontWeight:p.active?700:400, color:p.active?DK.text:DK.sub }}>{p.name}</span>
                {p.score!=null && <span style={{ fontSize:11, fontWeight:700, color:DK.crit }}>{p.score}</span>}
              </div>
              {p.status && <DKBadge label={p.status} color={p.statusColor}/>}
              {!p.score && <div style={{ height:3, borderRadius:2, background:'rgba(255,255,255,0.06)', marginTop:6 }}/>}
              {p.score!=null && <div style={{ height:3, borderRadius:2, background:'rgba(255,255,255,0.06)', marginTop:6, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${p.score}%`, background:DK.crit, borderRadius:2 }}/>
              </div>}
            </div>
          ))}
        </div>
        <div style={{ padding:'0 14px' }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:DK.faint, marginBottom:8 }}>↗ PORTFOLIO AVERAGE</div>
          <div style={{ height:4, borderRadius:2, background:'rgba(255,255,255,0.06)', marginBottom:6, overflow:'hidden' }}>
            <div style={{ height:'100%', width:'18%', background:DK.crit, borderRadius:2 }}/>
          </div>
          <div style={{ fontSize:11, color:DK.crit, fontWeight:700 }}>18</div>
          <div style={{ fontSize:10, color:DK.faint, marginTop:2 }}>1 project scored</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1, overflow:'auto', padding:20 }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
          <div style={{ width:34, height:34, borderRadius:9, background:'rgba(0,212,170,0.12)', border:'1px solid rgba(0,212,170,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🧠</div>
          <div>
            <h2 style={{ fontSize:18, fontWeight:800, color:DK.text, margin:0 }}>AI Readiness Scorecard</h2>
            <p style={{ fontSize:11, color:DK.sub, margin:0 }}>Analyse legacy codebases and identify exactly what needs to change before AI integration</p>
          </div>
        </div>

        {/* Score panel */}
        <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:12, padding:20, marginBottom:16, display:'grid', gridTemplateColumns:'auto 1fr', gap:24, alignItems:'start' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:11, fontWeight:700, color:DK.faint, letterSpacing:1, marginBottom:8 }}>healthbridge</div>
            <div style={{ display:'flex', alignItems:'baseline', gap:4, justifyContent:'center', marginBottom:4 }}>
              <span style={{ fontSize:48, fontWeight:900, color:DK.crit, lineHeight:1 }}>18</span>
              <span style={{ fontSize:20, color:DK.faint }}>/100</span>
            </div>
            <div style={{ fontSize:10, color:DK.sub, marginBottom:8 }}>AI Readiness Score</div>
            <DKBadge label="Not AI-Ready" color={DK.crit}/>
          </div>
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
              {[['0/20','Checks passed',DK.crit],['5','Critical gaps',DK.crit],['14–24w','Est. effort',DK.warn]].map(([v,l,c])=>(
                <div key={l} style={{ background:DK.card, border:`1px solid ${DK.border}`, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:20, fontWeight:800, color:c, marginBottom:2 }}>{v}</div>
                  <div style={{ fontSize:10, color:DK.sub }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:10, color:DK.faint, display:'flex', gap:8, alignItems:'center' }}>
              <span>🔍 Discovery run 65f0bb4f… · 3/8/2026</span>
            </div>
          </div>
        </div>

        {/* Critical gaps */}
        <div style={{ background:DK.panel, border:`1px solid rgba(239,68,68,0.2)`, borderRadius:12, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:DK.crit, letterSpacing:1.5, textTransform:'uppercase', marginBottom:12 }}>⚠ CRITICAL GAPS — MUST RESOLVE BEFORE AI INTEGRATION</div>
          {gaps.map((g,i)=>(
            <div key={i} style={{ display:'flex', gap:10, marginBottom:8, alignItems:'flex-start' }}>
              <span style={{ fontSize:11, fontWeight:700, color:DK.crit, flexShrink:0, marginTop:1 }}>{i+1}.</span>
              <span style={{ fontSize:11, color:DK.sub, lineHeight:1.55 }}>{g}</span>
            </div>
          ))}
        </div>

        {/* 5-dimension breakdown */}
        <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:12, padding:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:DK.faint, letterSpacing:1.5, textTransform:'uppercase', marginBottom:14 }}>5-DIMENSION BREAKDOWN</div>
          {dims.map((d,i)=>(
            <div key={i} style={{ marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <span style={{ fontSize:12, fontWeight:500, color:DK.text }}>{d.label}</span>
                <span style={{ fontSize:13, fontWeight:700, color:d.color }}>{d.score}</span>
              </div>
              <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:100, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${d.score}%`, background:d.color, borderRadius:100, opacity:.85 }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RevModernizeHITLDashboard() {
  const [filter, setFilter] = useState('all');
  const decisions = [
    { name:'PKG_FORM_UTILS',   sev:'Critical', desc:'No signal from name or package — DBA summary required for PKG_FORM_UTILS.' },
    { name:'PKG_AUDIT',        sev:'Critical', desc:'No signal from name or package — DBA summary required for PKG_AUDIT.' },
    { name:'PKG_COMPLIANCE',   sev:'Critical', desc:'No signal from name or package — DBA summary required for PKG_COMPLIANCE.' },
    { name:'PKG_CUSTOMER',     sev:'Critical', desc:'No signal from name or package — DBA summary required for PKG_CUSTOMER.' },
    { name:'PKG_LABRESULTS',   sev:'Critical', desc:'No signal from name or package — DBA summary required for PKG_LABRESULTS.' },
    { name:'PKG_SCHEDULING',   sev:'High',     desc:'Moderate complexity — business logic may span multiple domains.' },
    { name:'PKG_BILLING',      sev:'High',     desc:'High transaction volume — architectural placement affects DB performance.' },
    { name:'PKG_REPORTING',    sev:'High',     desc:'Cross-table aggregation — evaluate if API layer is appropriate.' },
    { name:'sp_SendCriticalAlert', sev:'High', desc:'Timing-sensitive logic — latency requirements must be assessed.' },
    { name:'sp_BatchProcessClaims',sev:'Medium',desc:'Batch workload — may be more efficient remaining in DB layer.' },
  ];
  const sevColor = s => s==='Critical'?DK.crit:s==='High'?DK.warn:'#60A5FA';
  const shown = filter==='all'?decisions:decisions.filter(d=>d.sev.toLowerCase()===filter);

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:DK.bg, minHeight:'100%', color:DK.text }}>
      {/* Header */}
      <div style={{ background:DK.panel, borderBottom:`1px solid ${DK.border}`, padding:'14px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
          <h2 style={{ fontSize:18, fontWeight:800, color:DK.text, margin:0 }}>HITL Decision Center</h2>
          <DKBadge label="AI-generated decisions requiring human judgment" color={DK.teal}/>
          <DKBadge label="23 pending" color={DK.warn}/>
          <DKBadge label="cached" color={DK.faint}/>
        </div>
        {/* Type stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:10 }}>
          {[['⚡','23','Stored Proc',DK.warn],['🔒','0','Security',DK.faint],['🖥','0','UI Component',DK.faint],['📐','0','Architecture',DK.faint],['🗄','0','Data Migration',DK.faint]].map(([icon,v,l,c])=>(
            <div key={l} style={{ background:DK.card, border:`1px solid ${DK.border}`, borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:9, color:DK.faint, marginBottom:4 }}>{icon}</div>
              <div style={{ fontSize:22, fontWeight:900, color:c }}>{v}</div>
              <div style={{ fontSize:10, color:DK.sub }}>{l}</div>
            </div>
          ))}
        </div>
        {/* Severity stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
          {[['Critical','9',DK.crit,'rgba(239,68,68,0.1)'],['High','13',DK.warn,'rgba(245,158,11,0.1)'],['Medium','1','#60A5FA','rgba(96,165,250,0.1)'],['Low','0',DK.faint,'transparent']].map(([l,v,c,bg])=>(
            <div key={l} style={{ background:bg, border:`1px solid ${c}30`, borderRadius:8, padding:'8px 12px', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:c, flexShrink:0 }}/>
              <span style={{ fontSize:11, color:DK.sub }}>{l}</span>
              <span style={{ fontSize:18, fontWeight:900, color:c, marginLeft:'auto' }}>{v}</span>
            </div>
          ))}
        </div>
        {/* Filters */}
        <div style={{ display:'flex', gap:6 }}>
          {[['all','All (23)'],['critical','Critical (9)'],['high','High (13)'],['medium','Medium (1)']].map(([k,l])=>(
            <button key={k} onClick={()=>setFilter(k)} style={{ padding:'4px 12px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:filter===k?700:400, background:filter===k?`rgba(0,212,170,0.12)`:'transparent', border:`1px solid ${filter===k?`rgba(0,212,170,0.3)`:`${DK.border}`}`, color:filter===k?DK.teal:DK.sub }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Decision list */}
      <div style={{ padding:'16px 24px' }}>
        <div style={{ fontSize:11, fontWeight:600, color:DK.faint, marginBottom:12 }}>PENDING REVIEW ({shown.length})</div>
        {shown.map((dec,i)=>(
          <div key={i} style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:10, padding:'14px 16px', marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <span style={{ fontSize:14, color:DK.warn }}>⚡</span>
              <span style={{ fontSize:13, fontWeight:700, color:DK.text, fontFamily:"'DM Mono',monospace" }}>{dec.name}</span>
              <DKBadge label={dec.sev} color={sevColor(dec.sev)}/>
              <DKBadge label="Stored Proc" color={DK.teal}/>
              <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:10, color:DK.faint }}>CONFIDENCE</span>
                <div style={{ width:40, height:4, borderRadius:2, background:'rgba(255,255,255,0.1)', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:'25%', background:DK.crit, borderRadius:2 }}/>
                </div>
                <span style={{ fontSize:10, fontWeight:700, color:DK.crit }}>25%</span>
              </div>
            </div>
            <div style={{ fontSize:11, color:DK.faint, marginBottom:4 }}>AI Architect · <span style={{ color:DK.warn }}>25% confidence</span> · Recommends: review_needed</div>
            <div style={{ fontSize:12, color:DK.sub, marginBottom:12, lineHeight:1.5 }}>{dec.desc}</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
              <button style={{ padding:'5px 12px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, background:'rgba(0,212,170,0.1)', border:'1px solid rgba(0,212,170,0.25)', color:DK.teal }}>→ Move to FastAPI <span style={{ opacity:.6 }}>1-2 days</span></button>
              <button style={{ padding:'5px 12px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:400, background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.2)', color:'#60A5FA' }}>Keep in DB <span style={{ opacity:.6, fontSize:10 }}>RevMigrate handles</span></button>
              <button style={{ padding:'5px 12px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:400, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', color:DK.warn }}>Hybrid <span style={{ opacity:.6 }}>2-3 days</span></button>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ flex:1, background:'rgba(255,255,255,0.04)', border:`1px solid ${DK.border}`, borderRadius:6, padding:'7px 12px', fontSize:11, color:DK.faint }}>Add notes…</div>
              <button style={{ padding:'7px 16px', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:700, background:'rgba(16,185,129,0.15)', border:'1px solid rgba(16,185,129,0.3)', color:DK.ok }}>✓ Approve</button>
              <button style={{ padding:'7px 16px', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:700, background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', color:DK.crit }}>✕ Reject</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevModernizePostTransformDashboard() {
  const procs = [
    { name:'sp_RegisterPatient',          endpoint:'POST /api/v1/patient',             risk:'medium' },
    { name:'sp_VerifyInsuranceEligibility',endpoint:'POST /api/v1/insuranceeligibility',risk:'medium' },
    { name:'sp_CreateLabOrder',            endpoint:'POST /api/v1/laborder',            risk:'medium' },
    { name:'sp_RecordLabResult',           endpoint:'POST /api/v1/recordlabresult',     risk:'medium' },
    { name:'sp_AuthenticateUser',          endpoint:'POST /api/v1/authenticateuser',    risk:'medium' },
  ];
  const dims = [
    { label:'Data Readiness',           before:20, after:48,  gain:28  },
    { label:'API & Agent Integration',  before:15, after:85,  gain:70  },
    { label:'Observability & Feedback', before:10, after:78,  gain:68  },
    { label:'Context & Memory',         before:10, after:75,  gain:65  },
    { label:'Governance & Trust',       before:35, after:82,  gain:47  },
  ];
  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:DK.bg, minHeight:'100%', color:DK.text, padding:24 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:16 }}>✨</span>
          <h2 style={{ fontSize:18, fontWeight:800, color:DK.text, margin:0 }}>Post-Transform Projection</h2>
        </div>
        <div style={{ fontSize:11, color:DK.faint }}>Based on 16 procs · 17 tables</div>
      </div>

      {/* Score transformation */}
      <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:14, padding:'24px', marginBottom:20, textAlign:'center' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:32 }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:DK.faint, marginBottom:10 }}>TODAY</div>
            <div style={{ position:'relative', width:80, height:80, margin:'0 auto 8px' }}>
              <svg viewBox="0 0 36 36" style={{ width:80, height:80, transform:'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={DK.crit} strokeWidth="2.5" strokeDasharray="18 82" strokeLinecap="round"/>
              </svg>
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:DK.crit }}>18</div>
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:DK.crit }}>AI-Blocked</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
            <div style={{ fontSize:20, color:DK.sub }}>→</div>
            <div style={{ fontSize:14, fontWeight:800, color:DK.teal }}>+54 pts</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:DK.faint, marginBottom:10 }}>AFTER TRANSFORM</div>
            <div style={{ position:'relative', width:80, height:80, margin:'0 auto 8px' }}>
              <svg viewBox="0 0 36 36" style={{ width:80, height:80, transform:'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={DK.teal} strokeWidth="2.5" strokeDasharray="72 28" strokeLinecap="round"/>
              </svg>
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:DK.teal }}>72</div>
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:DK.teal }}>AI-Ready</div>
          </div>
        </div>
        <div style={{ marginTop:16, fontSize:12, color:DK.sub }}>
          ⏱ Estimated delivery: <strong style={{ color:DK.text }}>3–7 weeks</strong> <span style={{ color:DK.faint }}>(vs 10–16 unassisted)</span>
        </div>
      </div>

      {/* Stored proc ownership split */}
      <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontSize:13, fontWeight:700, color:DK.text }}>Stored Proc Ownership Split</div>
          <div style={{ fontSize:11, color:DK.faint }}>16 total discovered</div>
        </div>
        <div style={{ height:10, borderRadius:100, overflow:'hidden', display:'flex', marginBottom:10 }}>
          <div style={{ width:'37.5%', background:DK.purple }}/>
          <div style={{ width:'12.5%', background:DK.teal }}/>
          <div style={{ width:'50%', background:DK.warn }}/>
        </div>
        <div style={{ display:'flex', gap:16, fontSize:11, marginBottom:12 }}>
          <div style={{ display:'flex', gap:5, alignItems:'center' }}><div style={{ width:8, height:8, borderRadius:'50%', background:DK.purple }}/><span style={{ color:DK.sub }}>RevModernize (6 procs → API)</span></div>
          <div style={{ display:'flex', gap:5, alignItems:'center' }}><div style={{ width:8, height:8, borderRadius:'50%', background:DK.teal }}/><span style={{ color:DK.sub }}>RevMigrate (2 procs → DB)</span></div>
          <div style={{ display:'flex', gap:5, alignItems:'center' }}><div style={{ width:8, height:8, borderRadius:'50%', background:DK.warn }}/><span style={{ color:DK.sub }}>Review needed (8)</span></div>
        </div>
        <div style={{ fontSize:11, color:DK.warn, marginBottom:12 }}>⚠ 8 procs flagged for human review (high complexity)</div>
        <div style={{ fontSize:11, fontWeight:600, color:DK.purple, marginBottom:8 }}>RevModernize → FastAPI endpoints</div>
        {procs.map((p,i)=>(
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${DK.border}` }}>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:DK.text, fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{p.name}</div>
              <div style={{ fontSize:10, color:DK.faint, fontFamily:"'DM Mono',monospace" }}>{p.endpoint}</div>
            </div>
            <DKBadge label={p.risk} color={DK.warn}/>
          </div>
        ))}
        <div style={{ fontSize:11, color:DK.teal, marginTop:8, cursor:'pointer' }}>∨ Show 1 more</div>
      </div>

      {/* Dimension Breakdown */}
      <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:700, color:DK.text, marginBottom:14 }}>Dimension Breakdown</div>
        {dims.map((d,i)=>(
          <div key={i} style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:12, color:DK.sub }}>{d.label}</span>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:DK.faint }}>{d.before} → <strong style={{ color:DK.text }}>{d.after}</strong></span>
                <span style={{ padding:'2px 8px', borderRadius:100, fontSize:10, fontWeight:700, color:DK.teal, background:'rgba(0,212,170,0.12)', border:'1px solid rgba(0,212,170,0.2)' }}>↑ +{d.gain}</span>
              </div>
            </div>
            <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:100, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${d.after}%`, background:`linear-gradient(to right,${DK.purple},${DK.teal})`, borderRadius:100 }}/>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:10, padding:'16px 20px' }}>
          <div style={{ fontSize:11, color:DK.sub, marginBottom:4 }}>RevModernize generates</div>
          <div style={{ fontSize:32, fontWeight:900, color:DK.purple }}>21</div>
          <div style={{ fontSize:11, color:DK.sub }}>production artifacts</div>
        </div>
        <div style={{ background:DK.panel, border:`1px solid ${DK.border}`, borderRadius:10, padding:'16px 20px' }}>
          <div style={{ fontSize:11, color:DK.sub, marginBottom:4 }}>RevMigrate handles</div>
          <div style={{ fontSize:32, fontWeight:900, color:DK.teal }}>5</div>
          <div style={{ fontSize:11, color:DK.sub }}>DB migration scripts</div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   DASHBOARD MODAL
   ════════════════════════════════════════════════════════════════════════════ */
const DASHBOARD_MAP = {
  exec:       { title:'AI Governance Executive Dashboard', url:'revsec.revupai.com/exec-dashboard',    component:ExecDashboard },
  agentIAM:   { title:'Agent IAM',                         url:'revsec.revupai.com/agent-iam',          component:AgentIAMDashboard },
  vendor:     { title:'Vendor Contracts · Grammarly',      url:'revsec.revupai.com/vendors/grammarly',  component:VendorDashboard },
  board:      { title:'Board Report Q1 2025',              url:'revsec.revupai.com/reports/q1-2025',    component:BoardReportDashboard },
  // ── RevMigrate dashboards ──────────────────────────────────────────────────
  revmigrate:   { title:'RevMigrate · AI DBA Dashboard',         url:'revmigrate.revupai.com/Oracle_Prod_DB',          component:RevMigrateDashboard,           props:{ initialTab:'overview' } },
  aiscout:      { title:'AI Scout — Migration Effort',           url:'revmigrate.revupai.com/ai-scout',                component:RevMigrateDashboard,           props:{ initialTab:'ai_scout' } },
  costimpact:   { title:'Cost Impact — Oracle_Prod_DB',          url:'revmigrate.revupai.com/cost-impact',             component:RevMigrateDashboard,           props:{ initialTab:'cost_impact' } },
  // ── RevModernize dashboards ────────────────────────────────────────────────
  aireadiness:  { title:'AI Readiness Scorecard — healthbridge', url:'revmodernize.revupai.com/healthbridge/readiness', component:RevModernizeAIReadinessDashboard },
  hitldecisions:{ title:'HITL Decision Center',                  url:'revmodernize.revupai.com/healthbridge/hitl',      component:RevModernizeHITLDashboard },
  posttransform:{ title:'Post-Transform Projection',             url:'revmodernize.revupai.com/healthbridge/projection',component:RevModernizePostTransformDashboard },
};

function DashboardModal({ id, onClose }) {
  const cfg = DASHBOARD_MAP[id];
  if (!cfg) return null;
  const DashComp = cfg.component;
  const dashProps = cfg.props || {};
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(9,6,15,0.82)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24, animation:'fadeIn .2s ease' }}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:1100, height:'85vh', borderRadius:14, overflow:'hidden', boxShadow:'0 40px 120px rgba(0,0,0,.8),0 0 0 1px rgba(255,255,255,.1)', display:'flex', flexDirection:'column', animation:'modalSlideUp .25s ease' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'#EEE9F8', borderBottom:'1px solid rgba(124,58,237,.15)', flexShrink:0 }}>
          <div style={{ display:'flex', gap:6 }}>
            {['#FF5F56','#FFBD2E','#27C93F'].map((c,i)=>(
              <div key={i} onClick={i===0?onClose:undefined} style={{ width:10, height:10, borderRadius:'50%', background:c, cursor:i===0?'pointer':'default' }}/>
            ))}
          </div>
          <div style={{ flex:1, background:'rgba(124,58,237,.06)', border:'1px solid rgba(124,58,237,.12)', borderRadius:6, padding:'4px 12px' }}>
            <span style={{ fontSize:11, color:'#3D2F6B', fontFamily:"'DM Mono',monospace" }}>{cfg.url}</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:100, background:'rgba(16,185,129,.1)', border:'1px solid rgba(16,185,129,.25)' }}>
            <div style={{ width:5, height:5, borderRadius:'50%', background:'#10B981', animation:'pulse 2s infinite' }}/>
            <span style={{ fontSize:9, fontWeight:700, color:'#10B981', letterSpacing:.8 }}>LIVE</span>
          </div>
          <button onClick={onClose} style={{ background:'rgba(124,58,237,.06)', border:'1px solid rgba(255,255,255,.1)', color:'#4A3A78', width:26, height:26, borderRadius:6, cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
        <div style={{ flex:1, overflow:'auto' }}><DashComp {...dashProps}/></div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   CHAT COMPONENTS
   ════════════════════════════════════════════════════════════════════════════ */
const LINK_ICONS = {
  chart:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  shield:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  contract: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  report:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  database: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  cost:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
};

function LinkCard({ step, color, rgb, onOpen }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={()=>onOpen(step.dashboard)} onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}
      style={{ margin:'4px 0 12px', borderRadius:10, cursor:'pointer', background:hovered?`rgba(${rgb},0.06)`:'#fff', border:`1px solid ${hovered?`rgba(${rgb},0.35)`:'rgba(0,0,0,0.08)'}`, padding:'11px 14px', display:'flex', alignItems:'center', gap:12, transition:'all .18s ease', boxShadow:hovered?`0 2px 12px rgba(${rgb},0.12)`:'0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, background:`rgba(${rgb},0.1)`, border:`1px solid rgba(${rgb},0.2)`, display:'flex', alignItems:'center', justifyContent:'center', color }}>{LINK_ICONS[step.icon]||LINK_ICONS.report}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'#1A0A2E', marginBottom:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{step.label}</div>
        <div style={{ fontSize:10, color:'#aaa', fontFamily:"'DM Mono',monospace" }}>{step.url}</div>
      </div>
      <div style={{ fontSize:11, fontWeight:600, color, opacity:hovered?1:0.6, display:'flex', alignItems:'center', gap:4, transition:'opacity .18s' }}>
        Open
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
      </div>
    </div>
  );
}

function UserMessage({ text, done }) {
  return (
    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:20 }}>
      <div style={{ maxWidth:'80%', padding:'11px 16px', borderRadius:'16px 16px 4px 16px', background:'#1A0A2E', border:'1px solid rgba(139,92,246,0.15)', fontSize:14, color:'#EDE9F8', lineHeight:1.65, fontFamily:"'DM Sans',system-ui,sans-serif", fontWeight:400, boxShadow:'0 2px 8px rgba(26,10,46,0.2)' }}>
        {text}
        {!done && <span style={{ opacity:0.45, animation:'blink .7s step-end infinite', marginLeft:1 }}>▋</span>}
      </div>
    </div>
  );
}

function AgentMessage({ scenario, steps, typing, onOpenDashboard }) {
  return (
    <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'flex-start' }}>
      <div style={{ width:38, height:38, borderRadius:11, flexShrink:0, background:`linear-gradient(135deg,${scenario.color} 0%,rgba(${scenario.rgb},0.72) 100%)`, boxShadow:`0 2px 10px rgba(${scenario.rgb},0.45)`, display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
        <RevUpMark size={26} color="#fff"/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, fontWeight:700, color:`rgba(${scenario.rgb},0.85)`, letterSpacing:1.8, textTransform:'uppercase', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
          <span>{scenario.agent}</span>
          <span style={{ opacity:.35, color:'#666' }}>·</span>
          <span style={{ fontWeight:400, color:'#888', letterSpacing:.8 }}>{scenario.role}</span>
        </div>
        {steps.map((s,i)=>{
          if(s.type==='text') return (
            <p key={i} style={{ fontSize:14, color:'#2D2D3A', lineHeight:1.7, margin:'0 0 12px', fontFamily:"'DM Sans',system-ui,sans-serif", fontWeight:400 }}>
              {s.rendered}
              {i===steps.length-1&&typing&&<span style={{ opacity:0.45, animation:'blink .7s step-end infinite', marginLeft:1, color:scenario.color }}>▋</span>}
            </p>
          );
          if(s.type==='card') return (
            <div key={i} style={{ margin:'4px 0 14px', background:'#fff', border:`1px solid rgba(${scenario.rgb},0.2)`, borderRadius:10, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ padding:'8px 14px', borderBottom:`1px solid rgba(${scenario.rgb},0.1)`, background:`rgba(${scenario.rgb},0.04)`, fontSize:10, fontWeight:700, letterSpacing:1.8, textTransform:'uppercase', color:`rgba(${scenario.rgb},0.8)` }}>{s.data.label}</div>
              <div style={{ padding:'10px 14px', display:'flex', flexDirection:'column', gap:8 }}>
                {s.data.rows.map((r,j)=>(
                  <div key={j} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:12 }}>
                    <span style={{ fontSize:12, color:'#888', flexShrink:0 }}>{r.k}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:r.hi?scenario.color:'#2D2D3A', fontFamily:"'DM Mono','Fira Code',monospace", textAlign:'right' }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
          if(s.type==='actions') return (
            <div key={i} style={{ display:'flex', gap:7, flexWrap:'wrap', marginTop:6 }}>
              {s.items.map((label,j)=>(
                <button key={j} style={{ padding:'7px 14px', borderRadius:8, cursor:'pointer', background:j===0?`rgba(${scenario.rgb},0.1)`:'#f5f5f7', border:j===0?`1px solid rgba(${scenario.rgb},0.3)`:'1px solid #e5e5ea', color:j===0?scenario.color:'#555', fontSize:12, fontWeight:j===0?600:400, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                  {j===0&&<span style={{ marginRight:4 }}>→</span>}{label}
                </button>
              ))}
            </div>
          );
          if(s.type==='link') return <LinkCard key={i} step={s} color={scenario.color} rgb={scenario.rgb} onOpen={onOpenDashboard}/>;
          return null;
        })}
        {typing&&steps.length===0&&(
          <div style={{ display:'flex', gap:5, padding:'6px 2px', alignItems:'center' }}>
            {[0,1,2].map(i=>(
              <div key={i} style={{ width:7, height:7, borderRadius:'50%', background:`rgba(${scenario.rgb},0.55)`, animation:`bounce 1.3s ease-in-out ${i*0.2}s infinite` }}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatWindow({ scenario }) {
  const [phase,setPhase]           = useState('idle');
  const [qText,setQText]           = useState('');
  const [aSteps,setASteps]         = useState([]);
  const [aTyping,setATyping]       = useState(false);
  const [activeDash,setActiveDash] = useState(null);
  const scrollRef   = useRef(null);
  const timerRef    = useRef([]);
  const scRef       = useRef(scenario);
  const dashOpenRef = useRef(false);

  useEffect(()=>{ dashOpenRef.current = !!activeDash; }, [activeDash]);
  useEffect(()=>{ scRef.current = scenario; }, [scenario]);

  const clearTimers = () => { timerRef.current.forEach(clearTimeout); timerRef.current = []; };
  const after = (ms,fn) => { const t=setTimeout(fn,ms); timerRef.current.push(t); return t; };
  const scrollBottom = () => requestAnimationFrame(()=>{ if(scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });

  const runSequence = useCallback((sc) => {
    clearTimers();
    setPhase('idle'); setQText(''); setASteps([]); setATyping(false);
    const q = sc.question;
    let delay = 500;
    setPhase('typing-q');
    q.split('').forEach((_,i) => { after(delay+i*TYPE_SPEED_Q,()=>{ setQText(q.slice(0,i+1)); scrollBottom(); }); });
    delay += q.length*TYPE_SPEED_Q + PAUSE_BEFORE;
    after(delay,()=>{ setPhase('thinking'); setATyping(true); scrollBottom(); });
    delay += 900;
    sc.response.forEach((step) => {
      if(step.type==='pause'){ delay+=step.ms; return; }
      if(step.type==='text'){
        const words = step.content.split(' ');
        after(delay,()=>{ setATyping(false); setASteps(prev=>[...prev,{ type:'text', rendered:'', raw:step.content }]); scrollBottom(); });
        words.forEach((w,wi)=>{ after(delay+wi*TYPE_SPEED_A*2.5,()=>{ setASteps(prev=>{ const next=[...prev]; const last={...next[next.length-1]}; last.rendered=words.slice(0,wi+1).join(' '); next[next.length-1]=last; return next; }); scrollBottom(); }); });
        delay += words.length*TYPE_SPEED_A*2.5+120; return;
      }
      if(['card','actions','link'].includes(step.type)){ after(delay,()=>{ setASteps(prev=>[...prev,step]); scrollBottom(); }); delay+=300; return; }
    });
    after(delay+200,()=>setPhase('done'));
    after(delay+LOOP_PAUSE,()=>{ if(!dashOpenRef.current) runSequence(scRef.current); });
  },[]);

  useEffect(()=>{ runSequence(scenario); return clearTimers; },[scenario.id]);

  const showQuestion = phase!=='idle';
  const showAgent    = ['thinking','typing-a','done'].includes(phase)||aSteps.length>0;

  return (
    <div style={{ borderRadius:14, overflow:'hidden', background:'#100D1A', border:'1px solid rgba(255,255,255,0.09)', boxShadow:`0 32px 80px rgba(0,0,0,0.6),0 0 0 1px rgba(255,255,255,0.06),0 0 60px rgba(${scenario.rgb},0.1)`, display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Browser chrome */}
      <div style={{ background:'#F5F3FC', borderBottom:'1px solid rgba(124,58,237,.12)', padding:'10px 16px', flexShrink:0, display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ display:'flex', gap:6 }}>
          {['#FF5F56','#FFBD2E','#27C93F'].map((c,i)=>(
            <div key={i} style={{ width:10, height:10, borderRadius:'50%', background:c, opacity:.75 }}/>
          ))}
        </div>
        <div style={{ flex:1, background:'rgba(124,58,237,.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:6, padding:'4px 12px', fontSize:11, color:'#3D2F6B', fontFamily:"'DM Mono','Fira Code',monospace" }}>
          {scenario.agent.toLowerCase()}.revupai.com — AI Agent Chat
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:100, background:`rgba(${scenario.rgb},0.1)`, border:`1px solid rgba(${scenario.rgb},0.25)` }}>
          <div style={{ width:5, height:5, borderRadius:'50%', background:scenario.color, animation:'pulse 2s ease-in-out infinite' }}/>
          <span style={{ fontSize:10, fontWeight:700, color:scenario.color, letterSpacing:.8 }}>LIVE</span>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding:'20px 18px 12px', display:'flex', flexDirection:'column', background:'#F7F6FB', scrollbarWidth:'none' }}>
        {phase==='idle'&&(
          <div style={{ margin:'12px 0 20px', padding:'20px', background:'#fff', borderRadius:14, border:'1px solid rgba(0,0,0,0.06)', textAlign:'center' }}>
            <div style={{ width:62, height:62, borderRadius:16, margin:'0 auto 14px', background:`linear-gradient(135deg,${scenario.color} 0%,rgba(${scenario.rgb},0.65) 100%)`, boxShadow:`0 6px 22px rgba(${scenario.rgb},0.45)`, display:'flex', alignItems:'center', justifyContent:'center', padding:10 }}>
              <RevUpMark size={42} color="#fff"/>
            </div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1A0A2E', marginBottom:4 }}>Hi, I'm {scenario.agent}</div>
            <div style={{ fontSize:12, color:`rgba(${scenario.rgb},0.8)`, fontWeight:600, letterSpacing:1.5, textTransform:'uppercase' }}>{scenario.role}</div>
          </div>
        )}
        {phase!=='idle'&&(
          <div style={{ textAlign:'center', marginBottom:18, fontSize:11, color:'#aaa', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
            {scenario.agent} · {scenario.role} · Session started
          </div>
        )}
        {showQuestion&&<UserMessage text={qText} done={phase!=='typing-q'}/>}
        {showAgent&&<AgentMessage scenario={scenario} steps={aSteps} typing={aTyping||(phase==='thinking'&&aSteps.length===0)} onOpenDashboard={setActiveDash}/>}
      </div>

      {/* Input bar */}
      <div style={{ padding:'10px 12px', flexShrink:0, borderTop:'1px solid #e8e6f0', background:'#fff', display:'flex', alignItems:'center', gap:9 }}>
        <div style={{ flex:1, background:'#F7F6FB', border:'1px solid #e2e0ec', borderRadius:10, padding:'9px 14px', fontSize:13, color:'#bbb', fontFamily:"'DM Sans',system-ui,sans-serif" }}>Ask {scenario.agent} anything…</div>
        <div style={{ width:34, height:34, borderRadius:9, flexShrink:0, background:'linear-gradient(135deg,#7C3AED,#5B21B6)', boxShadow:'0 2px 10px rgba(124,58,237,0.45)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>
        </div>
      </div>

      {activeDash&&<DashboardModal id={activeDash} onClose={()=>setActiveDash(null)}/>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   MEET AGENTS
   ════════════════════════════════════════════════════════════════════════════ */
const AGENTS = [
  { id:'revsec', name:'RevSec', role:'AI Security & Governance', route:'/ai-ciso', color:'#10B981', rgb:'16,185,129',
    outcome:'Risky AI actions blocked in real time. Shadow AI surfaced in 48h. Board report, written automatically.',
    stat:'$670K avg risk prevented',
    tagline:'Your operated AI security function — governing every AI your company runs, enforcing policy in real time, and proving it to your board.',
    caps:[
      { b:'Blocks, redacts, and quarantines in real time', t:' — not just alerts' },
      { b:'Governs every AI', t:' — coding assistants, MCP, models, SaaS, and agents — in every role' },
      { b:'Discovers every AI agent', t:' — registered, shadow, or unsanctioned' },
      { b:'Maps every action to your frameworks', t:' with board-ready, audit-grade evidence' },
      { b:'Operated with you', t:' — your frameworks mapped, your AI security function run' },
    ] },
  { id:'revmodernize', name:'RevModernize', role:'AI Architect', route:'/ai-architect', color:'#00D4AA', rgb:'0,212,170',
    outcome:'18 legacy apps AI-ready in 8–10 weeks. 47 new API endpoints. 18/100 → 91/100 readiness.',
    stat:'50%+ cost saving vs traditional',
    tagline:'Scans your actual codebase. Builds the AI-ready version. In weeks, not months.',
    caps:['Connects to your repos and produces an AI Readiness Scorecard','Generates a modernisation plan specific to your codebase','Human-in-the-loop approval on every critical architecture decision','Builds full stack — UI, APIs, vector stores, data layers','Hands off to AI CISO for compliance check before production'] },
  { id:'revmigrate', name:'RevMigrate', role:'AI DBA', route:'/ai-dba', color:'#F59E0B', rgb:'245,158,11',
    outcome:'Oracle to PostgreSQL in 8 weeks. $1.88M annual saving. Payback in under 3 months.',
    stat:'89% stored procedures auto-converted',
    tagline:'Migrates your Oracle or SQL Server estate. Then stays on as your permanent DBA.',
    caps:['Connects to Oracle, SQL Server and Sybase simultaneously','Maps every dependency — schema, stored procedures, code objects','Builds board-ready business case with exact savings','Automates 89% of stored procedure conversion','Provisions cloud environment and manages it post-migration'] },
  { id:'revtest', name:'RevTest', role:'AI QA', route:null, color:'#8B5CF6', rgb:'139,92,246',
    outcome:'Full regression in 38 minutes. 41% → 91% coverage. Self-healing tests. Zero backlog.',
    stat:'3.5 week test cycle → 38 minutes',
    tagline:'Full regression in 38 minutes. Self-healing tests. Zero maintenance backlog.',
    caps:['Generates test cases from your codebase and requirements','Builds and runs full regression suite hands-off','Self-heals broken tests when UI or code changes','Pixel-to-pixel visual comparison using AI vision models','Validates automatically after every migration or modernisation'] },
];

function MeetAgents() {
  const navigate = useNavigate();
  return (
    <section id="meet-agents" style={{ background:BG, padding:'clamp(80px,10vh,130px) clamp(40px,5vw,100px)', position:'relative', overflow:'hidden' }}>
      <div aria-hidden style={{ position:'absolute', top:'40%', left:'50%', transform:'translate(-50%,-50%)', width:900, height:600, borderRadius:'50%', background:'radial-gradient(ellipse,rgba(45,24,84,.35) 0%,transparent 70%)', filter:'blur(80px)', pointerEvents:'none' }}/>
      <div style={{ maxWidth:1400, margin:'0 auto', position:'relative', zIndex:2 }}>
        <div style={{ textAlign:'center', marginBottom:'clamp(48px,6vh,72px)' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px 5px 10px', borderRadius:100, marginBottom:28, background:'rgba(0,212,170,0.08)', border:'1px solid rgba(0,212,170,0.25)' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C2 }}/>
            <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(0,212,170,.8)' }}>The Platform</span>
          </div>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, letterSpacing:'-1.5px', lineHeight:1.08, fontSize:'clamp(32px,4vw,58px)', color:'#EDE9F8', margin:'0 0 18px' }}>
            Four AI roles. One platform.
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.2vw,17px)', fontWeight:300, lineHeight:1.7, color:'#9B90BA', maxWidth:520, margin:'0 auto' }}>
            Start with one. Each role operates independently — and hands off to the others when the work demands it.
          </p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:20 }}>
          {AGENTS.map(a=>(
            <article key={a.id}
              style={{ borderRadius:16, background:'rgba(255,255,255,0.025)', border:'1px solid rgba(255,255,255,0.08)', overflow:'hidden', display:'flex', flexDirection:'column', transition:'border-color .25s,transform .25s,box-shadow .25s' }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor=`rgba(${a.rgb},.35)`; e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 20px 56px rgba(0,0,0,.4),0 0 0 1px rgba(${a.rgb},.12)`; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
            >
              <div style={{ background:'#EDEAF6', padding:'16px 20px', display:'flex', alignItems:'center', gap:12, borderBottom:`2px solid rgba(${a.rgb},.25)` }}>
                <div style={{ width:38, height:38, borderRadius:10, flexShrink:0, background:`linear-gradient(135deg,${a.color} 0%,rgba(${a.rgb},0.7) 100%)`, boxShadow:`0 3px 10px rgba(${a.rgb},0.4)`, display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
                  <RevUpMark size={24} color="#fff"/>
                </div>
                <div>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:700, color:'#1A0A2E' }}>{a.name}</div>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${a.rgb},.7)`, filter:'brightness(0.7)' }}>{a.role}</div>
                </div>
              </div>
              <div style={{ padding:'14px 20px', minHeight:114, display:'flex', flexDirection:'column', justifyContent:'center', background:`rgba(${a.rgb},.05)`, borderBottom:`1px solid rgba(${a.rgb},.12)` }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, lineHeight:1.55, color:`rgba(${a.rgb},.9)`, margin:'0 0 6px', fontWeight:600 }}>{a.outcome}</p>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:`rgba(${a.rgb},.6)`, fontWeight:500 }}>→ {a.stat}</span>
              </div>
              <div style={{ padding:'20px 24px 22px', display:'flex', flexDirection:'column', flex:1 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, lineHeight:1.6, color:'#C8C0EC', margin:'0 0 18px', minHeight:84, fontStyle:'italic' }}>{a.tagline}</p>
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:9, marginBottom:22 }}>
                  {a.caps.map((cap,i)=>(
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                      <div style={{ width:4, height:4, borderRadius:'50%', flexShrink:0, background:a.color, opacity:.7, marginTop:7 }}/>
                      <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, lineHeight:1.55, color:'#C8C0EC' }}>{typeof cap==='object' ? <><strong style={{ color:'#EDE9F8', fontWeight:700 }}>{cap.b}</strong>{cap.t}</> : cap}</span>
                    </div>
                  ))}
                </div>
                <button onClick={()=>a.route&&navigate(a.route)}
                  style={{ padding:'10px 18px', borderRadius:9, cursor:a.route?'pointer':'not-allowed', background:`rgba(${a.rgb},0.08)`, border:`1px solid rgba(${a.rgb},0.25)`, color:a.route?a.color:`rgba(${a.rgb},0.4)`, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:600, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', opacity:a.route?1:0.5, transition:'all .2s ease' }}
                  onMouseEnter={e=>{ if(a.route) e.currentTarget.style.background=`rgba(${a.rgb},0.15)`; }}
                  onMouseLeave={e=>{ if(a.route) e.currentTarget.style.background=`rgba(${a.rgb},0.08)`; }}
                >
                  <span>Explore {a.name}</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   INTEGRATIONS STRIP
   ════════════════════════════════════════════════════════════════════════════ */
const AGENT_INTEGRATIONS = {
  revsec:{ name:'RevSec', color:'#10B981', rgb:'16,185,129', headline:'Sees and governs every AI tool across your stack.', categories:[{ label:'AI Models & APIs', items:['OpenAI GPT-4o','Anthropic Claude','Google Gemini','AWS Bedrock','Azure OpenAI','Mistral','Cohere','Groq','Llama 3','DeepSeek'] },{ label:'Agent Frameworks', items:['LangChain','LangGraph','AutoGen','CrewAI','Semantic Kernel','LlamaIndex','Haystack','n8n','Flowise','Dify'] },{ label:'Identity & Access', items:['Okta','Azure Active Directory','Ping Identity','Auth0','CyberArk','BeyondTrust','HashiCorp Vault','AWS IAM','Google Cloud IAM','ForgeRock'] },{ label:'Agent Platforms', items:['AWS Agents','Azure AI Foundry','Vertex AI Agents','Salesforce Agentforce','ServiceNow AI','GitHub Copilot','Workday AI','SAP Joule'] }] },
  revmigrate:{ name:'RevMigrate', color:'#F59E0B', rgb:'245,158,11', headline:'Migrates from any source database to any modern target.', categories:[{ label:'Legacy Sources', items:['Oracle 11g','Oracle 19c','IBM DB2','SQL Server 2008','SQL Server 2019','Sybase ASE','Teradata','Informix','MySQL 5.x','MariaDB'] },{ label:'Modern Targets', items:['PostgreSQL','Amazon Aurora','Google Cloud SQL','Azure SQL','CockroachDB','PlanetScale','Neon','Supabase','AlloyDB','Snowflake'] },{ label:'Cloud Warehouses', items:['Snowflake','Amazon Redshift','Google BigQuery','Azure Synapse','Databricks','dbt','Fivetran','Airbyte','Stitch'] },{ label:'Migration Tools', items:['AWS DMS','Azure Database Migration','Flyway','Liquibase','pgloader','ora2pg','AWS SCT'] }] },
  revmodernize:{ name:'RevModernize', color:'#00D4AA', rgb:'0,212,170', headline:'Reads and rebuilds any legacy codebase for the AI era.', categories:[{ label:'Legacy Languages', items:['COBOL','RPG','PL/I','Fortran','VB6','PowerBuilder','Delphi','ColdFusion','Classic ASP','Progress ABL'] },{ label:'Legacy Platforms', items:['IBM AS/400','CICS','IBM WebSphere','JBoss 4','Oracle WebLogic','BEA Tuxedo','TIBCO','MQ Series','Struts','EJB 2.x'] },{ label:'Modern Targets', items:['Spring Boot','Node.js','FastAPI','Next.js','.NET 8','Kubernetes','Docker','Kafka','GraphQL','REST APIs'] },{ label:'Cloud Platforms', items:['AWS','Azure','Google Cloud','Vercel','Cloudflare','Heroku','Railway','Render','Fly.io'] }] },
  revtest:{ name:'RevTest', color:'#8B5CF6', rgb:'139,92,246', headline:'Integrates with your entire delivery pipeline automatically.', categories:[{ label:'Test Frameworks', items:['Jest','Playwright','Cypress','Selenium','pytest','JUnit 5','TestNG','RSpec','Mocha','Vitest'] },{ label:'CI/CD Pipelines', items:['GitHub Actions','GitLab CI','Jenkins','CircleCI','Bitbucket Pipelines','TeamCity','Azure DevOps','Buildkite','ArgoCD'] },{ label:'Quality & Coverage', items:['SonarQube','Codecov','Coveralls','Allure','TestRail','Xray','Zephyr','qTest','PractiTest'] },{ label:'Monitoring', items:['Datadog','New Relic','Sentry','Grafana','PagerDuty','OpsGenie','Splunk','Elastic APM'] }] },
};

function IntegrationsStrip() {
  const [activeAgent,setActiveAgent]       = useState('revsec');
  const [activeCategory,setActiveCategory] = useState(0);
  const agentData = AGENT_INTEGRATIONS[activeAgent];
  const category  = agentData.categories[activeCategory];
  const items     = [...category.items,...category.items,...category.items];
  useEffect(()=>setActiveCategory(0),[activeAgent]);
  return (
    <section style={{ background:BG, borderTop:'1px solid rgba(255,255,255,0.05)', borderBottom:'1px solid rgba(255,255,255,0.05)', padding:'64px 0 56px', overflow:'hidden' }}>
      <style>{`@keyframes marqueeScroll{from{transform:translateX(0)}to{transform:translateX(-33.333%)}} .int-track{display:flex;gap:8px;width:max-content;animation:marqueeScroll 28s linear infinite;} .int-track:hover{animation-play-state:paused;}`}</style>
      <div style={{ padding:'0 clamp(40px,5vw,100px)', marginBottom:40 }}>
        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:'#C8C0EC', margin:'0 0 12px' }}>Integrations</p>
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', flexWrap:'wrap', gap:20 }}>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(22px,2.2vw,30px)', fontWeight:700, color:agentData.color, margin:0, lineHeight:1.3, maxWidth:560, transition:'color .3s ease' }}>{agentData.headline}</h2>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {Object.entries(AGENT_INTEGRATIONS).map(([id,a])=>{
              const sel=id===activeAgent;
              return (
                <button key={id} onClick={()=>setActiveAgent(id)} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 14px', borderRadius:9, cursor:'pointer', background:sel?`rgba(${a.rgb},0.12)`:'rgba(255,255,255,0.04)', border:`1px solid ${sel?`rgba(${a.rgb},0.4)`:'rgba(255,255,255,0.07)'}`, transition:'all .2s ease' }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:a.color, opacity:sel?1:0.7, flexShrink:0 }}/>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:sel?700:400, color:sel?'#ffffff':'#C8C0EC' }}>{a.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:8, padding:'0 clamp(40px,5vw,100px)', marginBottom:24, overflowX:'auto' }}>
        {agentData.categories.map((cat,i)=>{
          const sel=i===activeCategory;
          return (
            <button key={cat.label} onClick={()=>setActiveCategory(i)} style={{ padding:'5px 14px', borderRadius:7, cursor:'pointer', whiteSpace:'nowrap', background:sel?`rgba(${agentData.rgb},0.12)`:'transparent', border:`1px solid ${sel?`rgba(${agentData.rgb},0.35)`:'rgba(200,192,236,0.2)'}`, fontSize:12, fontWeight:sel?600:400, color:sel?agentData.color:'#C8C0EC', fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .2s ease', flexShrink:0 }}>{cat.label}</button>
          );
        })}
      </div>
      <div style={{ position:'relative' }}>
        <div aria-hidden style={{ position:'absolute', left:0, top:0, bottom:0, width:100, zIndex:2, background:'linear-gradient(to right,#09060F,transparent)', pointerEvents:'none' }}/>
        <div aria-hidden style={{ position:'absolute', right:0, top:0, bottom:0, width:100, zIndex:2, background:'linear-gradient(to left,#09060F,transparent)', pointerEvents:'none' }}/>
        <div className="int-track" key={`${activeAgent}-${activeCategory}`}>
          {items.map((name,i)=>(
            <div key={i}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 18px', borderRadius:9, flexShrink:0, background:'rgba(200,192,236,0.06)', border:'1px solid rgba(200,192,236,0.15)', cursor:'default', transition:'all .18s ease' }}
              onMouseEnter={e=>{ e.currentTarget.style.background=`rgba(${agentData.rgb},0.08)`; e.currentTarget.style.borderColor=`rgba(${agentData.rgb},0.25)`; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(200,192,236,0.06)'; e.currentTarget.style.borderColor='rgba(200,192,236,0.15)'; }}
            >
              <div style={{ width:5, height:5, borderRadius:'50%', background:agentData.color, opacity:.65, flexShrink:0 }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:500, color:'#DDD8F2' }}>{name}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={{ textAlign:'center', marginTop:36, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:'#C8C0EC' }}>
        Missing an integration?{' '}
        <a href="mailto:integrations@revupai.com" style={{ color:`rgba(${agentData.rgb},0.65)`, textDecoration:'none' }}>Let us know →</a>
      </p>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   LIVE DEMO SECTION
   ════════════════════════════════════════════════════════════════════════════ */
function LiveDemoSection({ demoRef }) {
  const [activeIdx, setActiveIdx] = useState(0);
  return (
    <section ref={demoRef} style={{ background:'#0D0A17', padding:'clamp(80px,10vh,120px) clamp(40px,5vw,100px)', borderTop:'1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:`rgba(${R2},.7)`, margin:'0 0 16px' }}>Live Demo</p>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(28px,3.5vw,48px)', lineHeight:1.1, letterSpacing:'-1.5px', color:'lavender', margin:'0 0 16px' }}>
            See an AI role working.
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:480, margin:'0 auto' }}>
            Real scenarios. Real decisions. Real outputs — the kind your team deals with every week.
          </p>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap', justifyContent:'center' }}>
          {SCENARIOS.map((sc,i)=>(
            <button key={sc.id} onClick={()=>setActiveIdx(i)}
              style={{ padding:'9px 18px', borderRadius:9, cursor:'pointer', background:i===activeIdx?`rgba(${sc.rgb},.12)`:'rgba(255,255,255,.04)', border:`1px solid ${i===activeIdx?`rgba(${sc.rgb},.35)`:'rgba(255,255,255,.08)'}`, fontSize:12.5, fontWeight:i===activeIdx?700:400, color:i===activeIdx?sc.color:'#9B90BA', fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .2s ease', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:sc.color, opacity:i===activeIdx?1:0.5 }}/>
              {sc.tab}
            </button>
          ))}
        </div>
        <div style={{ height:'clamp(480px,60vh,640px)' }}>
          <ChatWindow scenario={SCENARIOS[activeIdx]}/>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   HOME PAGE — orchestrator
   ════════════════════════════════════════════════════════════════════════════ */
export default function Home() {
  const demoRef = useRef(null);

  return (
    <div style={{ background:BG, minHeight:'100vh', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <Helmet>
        <title>RevUpAI — Operated AI · Roles Rebuilt as AI Agents</title>
        <meta name="description" content="RevUpAI deploys AI agents that do the job — not just assist with it. AI CISO, AI Architect, AI DBA and AI QA rebuilt as AI agents, operated by domain experts inside your enterprise from day one." />
        <meta name="keywords" content="operated AI, enterprise AI roles, secure AI enterprise, agentic AI process automation, AI agent deployment, enterprise AI automation, human in the loop AI" />
        <link rel="canonical" href="https://revupai.com" />
        <meta property="og:title" content="RevUpAI — Operated AI · Roles Rebuilt as AI Agents" />
        <meta property="og:description" content="AI agents that do the job, not just assist with it. Operated by domain experts inside your enterprise from day one." />
        <meta property="og:url" content="https://revupai.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RevUpAI — Operated AI · Roles Rebuilt as AI Agents" />
        <meta name="twitter:description" content="AI agents that do the job, not just assist with it. Operated by domain experts inside your enterprise from day one." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
      </Helmet>
      <style>{GLOBAL_CSS}</style>

      <HeroOperated/>
      <ProblemSection/>
      <ContrastSection/>
      <HITLSection/>
      <StatsBand/>
      <LiveDemoSection demoRef={demoRef}/>
      <MeetAgents/>
      <IntegrationsStrip/>
      <Problems/>
      <HowItWorks/>
      <DualCTA/>
    </div>
  );
}